import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../utils/api";
import styles from "./EditAppPage.module.scss";
import TabbedFormLayout from "../../components/TabbedFormLayout/TabbedFormLayout";
import CustomInput from "../../components/Inputs/CustomInput";
import CustomCheckbox from "../../components/CustomCheckbox/CustomCheckbox";

interface App {
  id: string;
  name: string;
  description?: string;
  isActive?: boolean;
  organizationId?: string;
  createdBy?: string;
  createdAt?: string;
}

export default function EditAppPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [appData, setAppData] = useState<App>({
    id: "",
    name: "",
    description: "",
    isActive: true,
    organizationId: "",
    createdBy: "",
    createdAt: "",
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  const fetchApp = async () => {
    try {
      const res = await api.post("/core/app/get", { appId: id });
      if (!res.data) {
        throw new Error("No app data returned");
      }
      setAppData(res.data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load app data";
      console.error("Failed to fetch app:", err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchApp();
  }, [id]);

  const handleSave = async () => {
    try {
      const updates = {
        name: appData.name,
        description: appData.description || null,
        isActive: appData.isActive ?? true // Ensure isActive is sent
      };
      console.log("Sending update payload:", { appId: id, updates }); // Debug log
      const response = await api.post("/core/app/update", { appId: id, updates });
      console.log("Update response:", response.data); // Debug log
      navigate("/apps");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to save app";
      console.error("Save failed:", err);
      setError(errorMessage);
    }
  };

  if (loading) return <div className={styles.loading}>Loading...</div>;
  if (error) return <div className={styles.error}>{error}</div>;

  const tabs = [
    {
      label: "Details",
      content: (
        <div className={styles.formGrid}>
          <CustomInput
            label="Name"
            value={appData.name || ""}
            onChange={(val) => setAppData({ ...appData, name: val })}
            type="text"
            required
            size="full"
          />
          <CustomInput
            label="Description"
            value={appData.description || ""}
            onChange={(val) => setAppData({ ...appData, description: val })}
            type="text"
            size="full"
          />
          <CustomCheckbox
            label="Active"
            checked={appData.isActive ?? true}
            onChange={(val) => setAppData({ ...appData, isActive: val })}
            id="isActive"
          />
          <CustomInput
            label="Organization ID"
            value={appData.organizationId || ""}
            onChange={() => {}}
            type="text"
            size="full"
            disabled
          />
          <CustomInput
            label="Created By"
            value={appData.createdBy || ""}
            onChange={() => {}}
            type="text"
            size="full"
            disabled
          />
          <CustomInput
            label="Created At"
            value={appData.createdAt ? new Date(appData.createdAt).toLocaleString() : ""}
            onChange={() => {}}
            type="text"
            size="full"
            disabled
          />
        </div>
      )
    }
  ];

  return (
    <div className={styles.container}>
      <div className={styles.title}>Edit App: {appData.name || "Loading..."}</div>
      <TabbedFormLayout
        tabs={tabs}
        onSave={handleSave}
        onCancel={() => navigate("/apps")}
        showActions={true}
      />
    </div>
  );
}
